import { useQueryClient } from "@tanstack/react-query";

import { UserPrivateRequestNickname } from "@features/privateRequest/PrivateRequestBar/UserPrivateRequestNickname";

import { Button } from "@components/Button";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { TGetPrivateRequestResponse } from "@api/privateRequests";

import { usePrivateRequestAcceptMutation, usePrivateRequestDeclineMutation } from "@storage/queries/privateRequest";

import { cn } from "@lib/utils";

import { QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_1000 } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

type UserPrivateRequestProps = {
	user: TGetPrivateRequestResponse;
};

export const UserPrivateRequest = ({ user }: UserPrivateRequestProps) => {
	const queryClient = useQueryClient();
	const { MOBILE, TABLET } = useLayoutBreakpoint();
	const privateRequestAcceptMutation = usePrivateRequestAcceptMutation();
	const privateRequestDeclineMutation = usePrivateRequestDeclineMutation();

	const isMobile = MOBILE || TABLET;

	const handleAcceptPrivate = async (id: number) => {
		privateRequestAcceptMutation.mutate(id);
		setTimeout(async () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		}, DELAY_TIME_1000);
	};

	const handleDeclinePrivate = async (id: number) => {
		privateRequestDeclineMutation.mutate(id);
		setTimeout(async () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		}, DELAY_TIME_1000);
	};

	return (
		<Stack
			direction="row"
			justify="between"
			align="center"
			wide
			className={cn("h-16 rounded-lg p-3", isMobile ? "bg-secondary" : "bg-block")}
		>
			<Stack>
				<UserPrivateRequestNickname user={user} />
				<Typography size="sm" padding="none" color="text">
					{user?.balance.toLocaleString("ru")} tk
				</Typography>
			</Stack>

			<Stack direction="row" spacing={8} align="center">
				{user.isWithClientAudio ? (
					<Button shape="circle" type="success" className="w-10" onClick={() => handleAcceptPrivate(user.id)}>
						<Image width={24} height={24} src={ICON_PATHS.PRIVATE_AUDIO_REQUEST} alt="privateAudioRequestIcon" />
					</Button>
				) : (
					<Button shape="circle" className="w-10" onClick={() => handleAcceptPrivate(user.id)}>
						<Image width={24} height={24} src={ICON_PATHS.PRIVATE_CHAT_REQUEST} alt="privateChatRequestIcon" />
					</Button>
				)}

				<Button
					shape="circle"
					type="glass"
					onClick={() => handleDeclinePrivate(user.id)}
					className="w-10 border border-[#ffffff33]"
				>
					<Image width={24} height={24} src={ICON_PATHS.PRIVATE_CHAT_CANCEL} alt="privateChatCancelIcon" />
				</Button>
			</Stack>
		</Stack>
	);
};
